import { Typography, UserTextRenderer } from "matsuri-ui"
import { css } from "@emotion/react"
import dayjs from "dayjs"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { useMemo } from "react"
import { InfoSection } from "./InfoSection"
import { GuestStayStatusChip } from "./GuestStayStatusChip"
import { EarlyCheckinChip } from "./EarlyCheckinChip"
import { useListingOccupancyByOperationIds } from "@/hooks/useListingOccupancyByOperationIds"
import { useTranslation } from "@/hooks/useTranslation"
import {
    OccupancyOperation,
    occupancyOperationStatusLabel
} from "@/model/listingOccupancy"
import { displayCleaningType } from "@/model/cleaningInfo"

const scheduleText = (op: OccupancyOperation) => {
    const start = op.scheduleStart
        ? dayjs.unix(op.scheduleStart).format("HH:mm")
        : undefined
    const end = op.scheduleEnd
        ? dayjs.unix(op.scheduleEnd).format("HH:mm")
        : undefined
    if (start && end) return `${start}~${end}`
    if (start) return `${start}~終了時刻未定`
    if (end) return `開始時刻未定~${end}`
    return undefined
}

/** 物件のゲスト滞在状況と、同日に同じ物件へ入る他の清掃・ツアー */
export const ListingOccupancyInfo = ({
    operationId
}: {
    operationId: string
}) => {
    const { t } = useTranslation()
    const { token } = useAuth()
    const operationIds = useMemo(() => [operationId], [operationId])
    const { data } = useListingOccupancyByOperationIds(token, operationIds)
    const occupancy = data?.[operationId]

    // 共用部など物件に紐づかない場合は表示しない
    if (!occupancy?.guest) return null

    return (
        <InfoSection>
            <Typography variant="h4" bolder>
                {t("物件の状況")}
            </Typography>
            <div>
                <Typography bolder>{t("ゲスト滞在状況")}</Typography>
                <div
                    css={css`
                        display: flex;
                        flex-wrap: wrap;
                        gap: 8px;
                        margin-top: 4px;
                    `}
                >
                    <GuestStayStatusChip status={occupancy.guest} />
                    {occupancy.hasEarlyCheckin && <EarlyCheckinChip />}
                </div>
            </div>
            <div>
                <Typography bolder>{t("同日の他の作業")}</Typography>
                {occupancy.operations.length === 0 ? (
                    <Typography>{t("なし")}</Typography>
                ) : (
                    <ul
                        css={css`
                            display: grid;
                            gap: 8px;
                            margin-top: 4px;
                            margin-left: 20px;
                        `}
                    >
                        {occupancy.operations.map(op => (
                            <li key={op.operationId}>
                                <Typography>
                                    <UserTextRenderer>
                                        {op.kind === "tour"
                                            ? (op.photoTourTitle ?? t("ツアー"))
                                            : op.cleaningType
                                              ? t(
                                                    displayCleaningType(
                                                        op.cleaningType
                                                    )
                                                )
                                              : t("清掃")}
                                    </UserTextRenderer>
                                </Typography>
                                <Typography variant="caption">
                                    {[
                                        op.companyName ?? undefined,
                                        scheduleText(op),
                                        t(
                                            occupancyOperationStatusLabel[
                                                op.status
                                            ]
                                        )
                                    ]
                                        .filter(Boolean)
                                        .join(" / ")}
                                </Typography>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </InfoSection>
    )
}
